import { useEffect, useState } from "react";
import BulkUploadButton from "../../components/BulkUploadButton";
import DataExportButton from "../../components/DataExportButton";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { ReceiptLongOutlined, EditNoteOutlined, Inventory2Outlined, TaskAltOutlined } from "@mui/icons-material";
import { SparePageHeader, SpareSummary, sparePageSx } from "../../components/SparePagePresentation";

import {
  AppDeleteDialog,
  AppSearchField,
  AppSnackbar,
  AppToolbar,
} from "../../../../components/common";

import PurchaseBillFormDialog from "../components/PurchaseBillFormDialog";
import PurchaseBillDetailsDialog from "../components/PurchaseBillDetailsDialog";
import PurchaseBillService from "../api/purchaseBill.service";
import PurchaseBillTable from "../components/PurchaseBillTable";
import { usePurchaseBills } from "../hooks/usePurchaseBills";
import { PurchaseBill, PurchaseBillPayload, SelectOption } from "../types";
import { filterRowsBySearch } from "../../utils/rowSearch";

const PurchaseBillsPage = () => {
  const {
    purchaseBills,
    fetchPurchaseBills,
    loading,
    pagination,
    filters,
    createPurchaseBill,
    updatePurchaseBill,
    deletePurchaseBill,
    updateStatus,
    updateFilters,
  } = usePurchaseBills();

  const [openForm, setOpenForm] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [openDetails, setOpenDetails] = useState(false);
  const [selectedPurchaseBill, setSelectedPurchaseBill] =
    useState<PurchaseBill | null>(null);
  const [receivingBill, setReceivingBill] =
    useState<PurchaseBill | null>(null);
  const [receivingStatus, setReceivingStatus] =
    useState<"RECEIVED" | "COMPLETED">("RECEIVED");
  const [receivingHubId, setReceivingHubId] = useState("");
  const [supplierOptions, setSupplierOptions] = useState<SelectOption[]>([]);
  const [sparePartOptions, setSparePartOptions] = useState<SelectOption[]>([]);
  const [hubOptions, setHubOptions] = useState<SelectOption[]>([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });

  useEffect(() => {
    PurchaseBillService.getSupplierOptions()
      .then(setSupplierOptions)
      .catch(() => setSupplierOptions([]));

    PurchaseBillService.getSparePartOptions()
      .then(setSparePartOptions)
      .catch(() => setSparePartOptions([]));

    PurchaseBillService.getHubOptions()
      .then(setHubOptions)
      .catch(() => setHubOptions([]));
  }, []);

  const handleCreate = () => {
    setSelectedPurchaseBill(null);
    setOpenForm(true);
  };

  const hubNameById = new Map(
    hubOptions.map((hub) => [hub.id, hub.name])
  );

  const purchaseBillRows = filterRowsBySearch(
    purchaseBills.map((bill) => ({
    ...bill,
    warehouseName: bill.warehouseId
      ? hubNameById.get(bill.warehouseId) || bill.warehouseId
      : "-",
    })),
    filters.search
  );

  const handleEdit = (purchaseBill: PurchaseBill) => {
    setSelectedPurchaseBill(purchaseBill);
    setOpenForm(true);
  };

  const handleView = (purchaseBill: PurchaseBill) => {
    setSelectedPurchaseBill(purchaseBill);
    setOpenDetails(true);
  };

  const handleCancel = async (purchaseBill: PurchaseBill) => {
    try {
      await updateStatus(purchaseBill.id, "CANCELLED");
      setSnackbar({
        open: true,
        message: "Purchase Bill cancelled successfully.",
        severity: "success",
      });
    } catch {
      setSnackbar({
        open: true,
        message: "Failed to cancel Purchase Bill.",
        severity: "error",
      });
    }
  };

  const submitStatusUpdate = async (
    purchaseBill: PurchaseBill,
    status: "RECEIVED" | "COMPLETED",
    warehouseId?: string | null
  ) => {
    try {
      await updateStatus(purchaseBill.id, status, warehouseId);
      setSnackbar({
        open: true,
        message:
          status === "RECEIVED"
            ? "Purchase Bill received and inventory updated."
            : "Purchase Bill completed successfully.",
        severity: "success",
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message:
          error?.response?.data?.message ||
          `Failed to ${status === "RECEIVED" ? "receive" : "complete"} Purchase Bill.`,
        severity: "error",
      });
    }
  };

  const openReceivingHubDialog = (
    purchaseBill: PurchaseBill,
    status: "RECEIVED" | "COMPLETED"
  ) => {
    setReceivingBill(purchaseBill);
    setReceivingStatus(status);
    setReceivingHubId(purchaseBill.warehouseId || "");
  };

  const handleReceive = async (purchaseBill: PurchaseBill) => {
    if (!purchaseBill.warehouseId) {
      openReceivingHubDialog(purchaseBill, "RECEIVED");
      return;
    }

    await submitStatusUpdate(purchaseBill, "RECEIVED", purchaseBill.warehouseId);
  };

  const handleComplete = async (purchaseBill: PurchaseBill) => {
    if (!purchaseBill.warehouseId) {
      openReceivingHubDialog(purchaseBill, "COMPLETED");
      return;
    }

    await submitStatusUpdate(purchaseBill, "COMPLETED", purchaseBill.warehouseId);
  };

  const handleReceiveDialogConfirm = async () => {
    if (!receivingBill || !receivingHubId) {
      setSnackbar({
        open: true,
        message: "Please select a Receiving Hub.",
        severity: "error",
      });
      return;
    }

    await submitStatusUpdate(receivingBill, receivingStatus, receivingHubId);
    setReceivingBill(null);
    setReceivingHubId("");
  };

  const handleDelete = (purchaseBill: PurchaseBill) => {
    setSelectedPurchaseBill(purchaseBill);
    setOpenDelete(true);
  };

  const handleSearch = (value: string) => {
    updateFilters({
      search: value,
      page: 1,
    });
  };

  const handleSubmit = async (payload: PurchaseBillPayload) => {
    try {
      if (selectedPurchaseBill) {
        await updatePurchaseBill(selectedPurchaseBill.id, payload);
        setSnackbar({
          open: true,
          message: "Purchase Bill updated successfully.",
          severity: "success",
        });
      } else {
        await createPurchaseBill(payload);
        setSnackbar({
          open: true,
          message: "Purchase Bill created successfully.",
          severity: "success",
        });
      }

      setOpenForm(false);
      setSelectedPurchaseBill(null);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message:
          error?.response?.data?.message ||
          "Failed to save Purchase Bill.",
        severity: "error",
      });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedPurchaseBill) return;

    try {
      await deletePurchaseBill(selectedPurchaseBill.id);
      setSnackbar({
        open: true,
        message: "Purchase Bill deleted successfully.",
        severity: "success",
      });
      setOpenDelete(false);
      setSelectedPurchaseBill(null);
    } catch {
      setSnackbar({
        open: true,
        message: "Failed to delete Purchase Bill.",
        severity: "error",
      });
    }
  };

  return (
    <Box
      sx={sparePageSx("purchases")}
    >
      <SparePageHeader kind="purchases"
        title="Purchase Bills"
        subtitle="Register supplier purchase bills and itemized spare costs."
        action={<Stack direction="row" flexWrap="wrap" gap={1}><BulkUploadButton kind="purchase-bills" onImported={fetchPurchaseBills} /><DataExportButton kind="purchase-bills" filters={filters} disabled={loading} /><Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>New Purchase Bill</Button></Stack>}
      />

      <SpareSummary items={[
        { label: "Total Purchase Bills", value: loading ? "—" : pagination.total, icon: <ReceiptLongOutlined />, color: "#7c3aed" },
        { label: "Drafts · this page", value: loading ? "—" : purchaseBills.filter((bill) => bill.status === "DRAFT").length, icon: <EditNoteOutlined />, color: "#64748b" },
        { label: "Received · this page", value: loading ? "—" : purchaseBills.filter((bill) => bill.status === "RECEIVED").length, icon: <Inventory2Outlined />, color: "#0284c7" },
        { label: "Completed · this page", value: loading ? "—" : purchaseBills.filter((bill) => bill.status === "COMPLETED").length, icon: <TaskAltOutlined />, color: "#10b981" },
      ]} />

      <AppToolbar
        left={
          <AppSearchField
            value={filters.search ?? ""}
            onChange={handleSearch}
            placeholder="Search purchase bills..."
          />
        }
      />

      <Stack mt={2}>
        <PurchaseBillTable
          rows={purchaseBillRows}
          loading={loading}
          page={(filters.page || 1) - 1}
          pageSize={filters.limit || 10}
          rowCount={pagination.total}
          onPageChange={(page) =>
            updateFilters({
              page: page + 1,
            })
          }
          onPageSizeChange={(pageSize) =>
            updateFilters({
              page: 1,
              limit: pageSize,
            })
          }
          onEdit={handleEdit}
          onView={handleView}
          onReceive={handleReceive}
          onComplete={handleComplete}
          onCancel={handleCancel}
          onDelete={handleDelete}
        />
      </Stack>

      <PurchaseBillFormDialog
        open={openForm}
        loading={loading}
        purchaseBill={selectedPurchaseBill}
        suppliers={supplierOptions}
        spareParts={sparePartOptions}
        hubs={hubOptions}
        onClose={() => {
          setOpenForm(false);
          setSelectedPurchaseBill(null);
        }}
        onSubmit={handleSubmit}
      />

      <PurchaseBillDetailsDialog
        open={openDetails}
        purchaseBill={selectedPurchaseBill}
        onClose={() => {
          setOpenDetails(false);
          setSelectedPurchaseBill(null);
        }}
      />

      <AppDeleteDialog
        open={openDelete}
        title="Delete Purchase Bill"
        content={`Are you sure you want to delete purchase bill "${
          selectedPurchaseBill?.code ?? ""
        }"? This will cancel the bill.`}
        loading={loading}
        onClose={() => {
          setOpenDelete(false);
          setSelectedPurchaseBill(null);
        }}
        onConfirm={handleDeleteConfirm}
      />

      <Dialog
        open={!!receivingBill}
        onClose={() => {
          setReceivingBill(null);
          setReceivingHubId("");
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Select Receiving Hub</DialogTitle>
        <DialogContent dividers>
          <TextField
            select
            fullWidth
            required
            label="Receiving Hub"
            value={receivingHubId}
            onChange={(event) => setReceivingHubId(event.target.value)}
            helperText={
              hubOptions.length
                ? "Inventory will be posted to this hub."
                : "Vehicle Service hubs are not loaded."
            }
          >
            {hubOptions.map((hub) => (
              <MenuItem key={hub.id} value={hub.id}>
                {hub.name}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            variant="outlined"
            disabled={loading}
            onClick={() => {
              setReceivingBill(null);
              setReceivingHubId("");
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={loading || !receivingHubId}
            onClick={handleReceiveDialogConfirm}
          >
            {receivingStatus === "RECEIVED" ? "Mark Received" : "Complete"}
          </Button>
        </DialogActions>
      </Dialog>

      <AppSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() =>
          setSnackbar((prev) => ({
            ...prev,
            open: false,
          }))
        }
      />
    </Box>
  );
};

export default PurchaseBillsPage;
